import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import { RecordSelect } from '@/components/RecordSelect';

import {
  BooleanFilterRowControl,
  MultiChoiceFilterRowControl,
  readBooleanFilterState,
  SearchFilterRowControl,
  SelectFilterRowControl,
  SingleChoiceFilterRowControl,
} from '../filter-row-controls';
import type { ColumnDefinition, ColumnHeaderFilter } from '../types';

import { operatorI18nKey } from './filter-operators';
import { FilterRowInputControl } from './FilterRowInputControl';

/**
 * Czytelny opis aktywnego filtra GRUPY (kolumny złożonej) do „echa" w wygaszonej komórce
 * pola składowego — operator + wartość (tryb warunku, jedyny używany przez kolumnę złożoną);
 * defensywnie obsługuje też range/values, gdyby echo dotyczyło innej kontrolki.
 */
function describeEchoFilter(
  filter: ColumnHeaderFilter,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (filter.mode === 'condition' && filter.operator) {
    const opLabel = t(operatorI18nKey(filter.operator));
    return filter.conditionValue ? `${opLabel}: ${filter.conditionValue}` : opLabel;
  }
  if (filter.mode === 'range') {
    return [filter.rangeMin, filter.rangeMax].filter(Boolean).join(' – ');
  }
  if (filter.mode === 'values') {
    return t('ui.configurableTable.filterRow.valuesSelected', {
      count: filter.selectedValues?.length ?? 0,
    });
  }
  return '';
}

/**
 * Dyspozytor pojedynczej komórki wiersza filtrów. Wybiera kontrolkę wg
 * `headerFilter.category` i respektuje bramkę R3. Kontrolki współdzielone
 * (boolean/select/single-choice/multi-choice/legacy-search) reużywane z
 * `../filter-row-controls` (oparte o `Dropdown`/`Input`, spójny wygląd + dark mode);
 * text/number/date obsługuje `FilterRowInputControl` — pole z lupą BEZ menu operatorów
 * (z buforem/debounce) — wzorzec DevExpress „Filter Row".
 *
 *  - `boolean` → dropdown Wszystkie/Tak/Nie (`mode:'condition'`+`operator:'eq'`).
 *  - `select` ze stałymi opcjami → dropdown exact-match (`mode:'values'`).
 *  - `single-choice` / `multi-choice` → checkboxy (`mode:'values'`, wartość ∈ zbiór);
 *    opt-in przez kategorię, ta sama ścieżka serwerowa co `select`.
 *  - `record-select` z opcjami → kompaktowy wybór rekordów (`RecordSelect`, `mode:'values'`).
 *  - Kategorie z listą opcji (`select` / `single-choice` / `multi-choice` / `record-select`) BEZ
 *    opcji nie mają typowanej kontrolki — trafiają do ostatniej gałęzi (poniżej: „Szukaj…” albo
 *    pusta komórka).
 *  - `text`/`number`/`date` → pole z lupą (`FilterRowInputControl`): wiersz nie pozwala WYBRAĆ
 *    operatora — nowy filtr działa na domyślnym operatorze kategorii (tekst „zawiera”, liczba i
 *    data „równa się”), inne operatory ustawia się w oknie przy nagłówku kolumny. Operator
 *    ustawiony w tym oknie wiersz ZACHOWUJE przy edycji wartości (`useFilterRowDraft` →
 *    `readOperatorFromFilter`; „pomiędzy” → para od–do, operatory bez wartości → brak pola);
 *    gdy filtr jest w trybie „wartości" (zaaplikowany z popupu nagłówka) →
 *    read-only chip z liczbą i „✕" (edycja wartości dalej przez popup).
 *  - BEZ typowanej kontrolki (brak `headerFilter`, `category: 'none'` albo kategoria z listą bez
 *    opcji): legacy pole „Szukaj…" piszące do `columnFilters` — renderowane, gdy
 *    `column.filterable !== false` (flaga domyślnie włączona; tylko ta gałąź ją czyta) I
 *    `columnFilters` jest sensowne (`columnFiltersMeaningful`): albo tabela filtruje LOKALNIE
 *    (`headerFilters` niekontrolowane → `columnFilters` też lokalny, np. Kartoteki admina /
 *    AdditionalViewTab), albo strona KONTROLUJE `columnFilters` (`onColumnFiltersChange` →
 *    serwer, np. `RecordTablePicker` z `category:'none'`+`fieldSlug`). Gdy tabela jest
 *    server-paginowana i NIE kontroluje `columnFilters` (5 ekranów procesów/KSeF) → pusty
 *    placeholder, żeby nie renderować pola filtrującego WYŁĄCZNIE pobraną stronę (cichy slice
 *    bug). Kolumna niefiltrowalna → pusty placeholder.
 */

type FilterRowColumn = Pick<ColumnDefinition, 'id' | 'header' | 'headerFilter' | 'filterable'>;

export interface FilterRowCellProps {
  column: FilterRowColumn;
  columnLabel: string;
  /** `headerFilters[column.id]`. */
  filter: ColumnHeaderFilter | undefined;
  /** `String(columnFilters[column.id] ?? '')` — legacy input. */
  columnFilterValue: string;
  /** Czy tabela lift-uje `headerFilters` (tryb serwerowy) — definicja „trybu kontrolowanego". */
  hasExternalHeaderFilters: boolean;
  /** Czy strona kontroluje `columnFilters` (`onColumnFiltersChange`) — decyduje, czy legacy
   *  input ma sens na tabeli server-paginowanej (inaczej filtrowałby tylko pobraną stronę). */
  hasExternalColumnFilters: boolean;
  onApplyHeaderFilter: (columnId: string, filter: ColumnHeaderFilter | null) => void;
  onColumnFilterChange: (columnId: string, value: string) => void;
  /** Kolumna wygaszona przez wzajemne wykluczanie grupy OR (kolumna złożona ↔ pola składowe). */
  disabled?: boolean;
  /** Filtr grupy do „echa" w wygaszonej komórce (fraza read-only). */
  echoFilter?: ColumnHeaderFilter;
  testIdPrefix?: string;
}

export function FilterRowCell({
  column,
  columnLabel,
  filter,
  columnFilterValue,
  hasExternalHeaderFilters,
  hasExternalColumnFilters,
  onApplyHeaderFilter,
  onColumnFilterChange,
  disabled,
  echoFilter,
  testIdPrefix,
}: FilterRowCellProps) {
  const { t } = useTranslation();
  const hf = column.headerFilter;
  const category = hf?.category;
  const hasOptions = (hf?.options?.length ?? 0) > 0;
  const applyHf = (next: ColumnHeaderFilter | null) => onApplyHeaderFilter(column.id, next);

  // Wygaszenie przez grupę OR (kolumna złożona aktywna → pola składowe wygaszone; i odwrotnie).
  // Read-only chip z frazą aktywnego filtra grupy — bez kontrolki (użytkownik zmienia filtr w
  // komórce, która grupę napędza). Filtr sąsiada jest już wyczyszczony ze stanu przez
  // `commitHeaderFilter`, więc echo NIE reprezentuje własnego wpisu tej kolumny.
  if (disabled) {
    const echoText = echoFilter ? describeEchoFilter(echoFilter, t) : '';
    return (
      <div
        className="inline-flex h-9 max-w-full items-center gap-1 rounded-lg border border-gray-200 bg-gray-100 px-2 text-gray-400 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-500"
        title={t('ui.configurableTable.filterRow.disabledByGroup')}
        data-testid={testIdPrefix ? `${testIdPrefix}-groupdisabled-${column.id}` : undefined}
      >
        <span className="truncate text-[11px]">
          {echoText || t('ui.configurableTable.filterRow.disabledByGroup')}
        </span>
      </div>
    );
  }

  if (category === 'boolean') {
    return (
      <BooleanFilterRowControl
        columnLabel={columnLabel}
        value={readBooleanFilterState(filter)}
        onChange={(next) => {
          if (next === 'all') {
            applyHf(null);
          } else {
            applyHf({
              field: column.id,
              mode: 'condition',
              operator: 'eq',
              conditionValue: next,
            });
          }
        }}
        testIdPrefix={testIdPrefix ? `${testIdPrefix}-boolfilter-${column.id}` : undefined}
      />
    );
  }

  if (category === 'select' && hasOptions) {
    return (
      <SelectFilterRowControl
        columnLabel={columnLabel}
        field={column.id}
        options={hf!.options!}
        filter={filter}
        onApply={applyHf}
        testId={testIdPrefix ? `${testIdPrefix}-selectfilter-${column.id}` : undefined}
      />
    );
  }

  if (category === 'single-choice' && hasOptions) {
    return (
      <SingleChoiceFilterRowControl
        columnLabel={columnLabel}
        field={column.id}
        options={hf!.options!}
        filter={filter}
        onApply={applyHf}
        testIdPrefix={testIdPrefix ? `${testIdPrefix}-singlechoicefilter-${column.id}` : undefined}
      />
    );
  }

  if (category === 'multi-choice' && hasOptions) {
    return (
      <MultiChoiceFilterRowControl
        columnLabel={columnLabel}
        field={column.id}
        options={hf!.options!}
        filter={filter}
        onApply={applyHf}
        testIdPrefix={testIdPrefix ? `${testIdPrefix}-multichoicefilter-${column.id}` : undefined}
      />
    );
  }

  if (category === 'record-select' && hasOptions) {
    const selectedValues =
      filter?.mode === 'values'
        ? (filter.selectedValues?.filter((v): v is string => v !== null) ?? [])
        : [];
    return (
      <RecordSelect
        value={selectedValues}
        onChange={(vals) =>
          applyHf(vals.length ? { field: column.id, mode: 'values', selectedValues: vals } : null)
        }
        options={hf!.options!}
        placeholder={columnLabel}
        compact
        data-testid={testIdPrefix ? `${testIdPrefix}-recordselectfilter-${column.id}` : undefined}
      />
    );
  }

  if (category === 'text' || category === 'number' || category === 'date') {
    // Filtr zaaplikowany przez popup w trybie „wartości" — read-only chip, żeby
    // pierwszy klawisz w edytorze nie skasował wielokrotnego wyboru z popupu.
    if (filter?.mode === 'values') {
      const count = filter.selectedValues?.length ?? 0;
      return (
        <div
          className="inline-flex h-9 items-center gap-1 rounded-lg border border-accent-200 bg-accent-50 px-2 dark:border-accent-800 dark:bg-accent-900/30"
          data-testid={testIdPrefix ? `${testIdPrefix}-valueschip-${column.id}` : undefined}
        >
          <span className="text-[11px] text-accent-700 dark:text-accent-300">
            {t('ui.configurableTable.filterRow.valuesSelected', { count })}
          </span>
          <button
            type="button"
            aria-label={`${t('ui.configurableTable.filterRow.clearAriaLabel')}: ${columnLabel}`}
            onClick={() => applyHf(null)}
            className="rounded p-0.5 text-accent-500 hover:bg-accent-100 hover:text-accent-700 dark:hover:bg-accent-800"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      );
    }
    return (
      <FilterRowInputControl
        columnId={column.id}
        columnLabel={columnLabel}
        kind={category}
        filter={filter}
        onApply={(f) => applyHf(f)}
        debounced={hasExternalHeaderFilters}
        testIdPrefix={testIdPrefix ? `${testIdPrefix}-filter-${column.id}` : undefined}
      />
    );
  }

  // Kolumna bez typowanej kontrolki (brak headerFilter, category: 'none' albo kategoria z listą
  // bez opcji): legacy pole „Szukaj…" piszące do columnFilters — tylko gdy filtrowalna I
  // columnFilters jest sensowne (tabela filtruje lokalnie LUB strona kontroluje columnFilters).
  // Inaczej (server-paginowana bez kontroli columnFilters) → pusty placeholder, patrz JSDoc.
  const columnFiltersMeaningful = !hasExternalHeaderFilters || hasExternalColumnFilters;
  if (column.filterable !== false && columnFiltersMeaningful) {
    return (
      <SearchFilterRowControl
        columnLabel={columnLabel}
        value={columnFilterValue}
        onChange={(value) => onColumnFilterChange(column.id, value)}
      />
    );
  }

  return <div className="h-8" />;
}
